/** 会议 Tab — 创建房间 / 加入房间 / 多方通话 + 内嵌聊天 */
import {
  CloseOutlined,
  PhoneOutlined,
  PoweroffOutlined,
  SendOutlined,
  TeamOutlined,
  UserAddOutlined,
  VideoCameraAddOutlined,
} from '@ant-design/icons';
import {
  Avatar,
  Button,
  Empty,
  Input,
  List,
  Modal,
  Select,
  Space,
  Spin,
  Tag,
  Typography,
  message,
} from 'antd';
import { useEffect, useRef, useState } from 'react';
import {
  type ChatMessage,
  type Conversation,
  type DirectoryUser,
  sipPhoneApi,
} from '../../api/sip-phone-api.js';
import { type SipState, sipService } from './shared.js';

interface Props {
  sipState: SipState;
}

function fmtTime(iso: string) {
  return new Date(iso).toLocaleTimeString('zh-CN', { hour: '2-digit', minute: '2-digit' });
}

function userOptionLabel(user: DirectoryUser) {
  return [user.name, user.extension].filter(Boolean).join(' ');
}

function appendUniqueMessage(prev: ChatMessage[], next: ChatMessage) {
  if (prev.some((item) => item.id === next.id)) return prev;
  return [...prev, next];
}

export function ConferenceTab({ sipState }: Props) {
  const [conf, setConf] = useState<Conversation | null>(null);
  const [users, setUsers] = useState<DirectoryUser[]>([]);
  const [creating, setCreating] = useState(false);
  const [confName, setConfName] = useState('');
  const [invitedIds, setInvitedIds] = useState<string[]>([]);
  const [joining, setJoining] = useState(false);
  const [joinCode, setJoinCode] = useState('');
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [draft, setDraft] = useState('');
  const [adding, setAdding] = useState(false);
  const [addId, setAddId] = useState('');
  const scrollRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if ((creating || adding) && users.length === 0) {
      sipPhoneApi
        .listEnterprise()
        .then(setUsers)
        .catch(() => {});
    }
  }, [creating, adding, users.length]);

  // 监听来自 backend 的会议邀请
  useEffect(() => {
    const handler = (ev: Event) => {
      const detail = (ev as CustomEvent).detail as {
        conversationId: string;
        roomCode: string;
        name: string;
        inviterName: string;
      };
      Modal.confirm({
        title: '会议邀请',
        content: `${detail.inviterName} 邀请你加入会议 "${detail.name}" (房间码 ${detail.roomCode})`,
        okText: '加入',
        cancelText: '稍后',
        onOk: async () => {
          try {
            const c = await sipPhoneApi.joinConference(detail.roomCode);
            await enterConference(c);
          } catch {
            message.error('加入会议失败');
          }
        },
      });
    };
    window.addEventListener('sip:conf-invite', handler as EventListener);
    return () => window.removeEventListener('sip:conf-invite', handler as EventListener);
  }, []);

  useEffect(() => {
    if (!conf) return;
    const handler = (ev: Event) => {
      const detail = (ev as CustomEvent<ChatMessage>).detail;
      if (detail.conversationId === conf.id) {
        setMessages((prev) => appendUniqueMessage(prev, detail));
        setTimeout(() => scrollRef.current?.scrollTo({ top: 999999 }), 30);
      }
    };
    window.addEventListener('sip:message', handler as EventListener);
    return () => window.removeEventListener('sip:message', handler as EventListener);
  }, [conf]);

  const enterConference = async (c: Conversation) => {
    setConf(c);
    if (c.roomCode) {
      const result = sipService.joinConferenceBridge(c.roomCode);
      if (!result.ok) message.error(result.reason ?? '呼入会议桥失败');
    } else {
      message.error('会议房间缺少房间码，无法呼入会议桥');
    }
    try {
      const msgs = await sipPhoneApi.listMessages(c.id);
      setMessages(msgs);
    } catch {
      /* ignore */
    }
  };

  const handleCreate = async () => {
    try {
      const c = await sipPhoneApi.createConference(confName.trim() || '即时会议', invitedIds);
      setCreating(false);
      setConfName('');
      setInvitedIds([]);
      message.success('会议房间已创建');
      await enterConference(c);
    } catch {
      message.error('创建会议失败');
    }
  };

  const handleJoin = async () => {
    if (!joinCode.trim()) return;
    try {
      const c = await sipPhoneApi.joinConference(joinCode.trim().toUpperCase());
      setJoining(false);
      setJoinCode('');
      await enterConference(c);
    } catch {
      message.error('加入失败 — 房间码错误或不存在');
    }
  };

  const handleAddMember = async () => {
    if (!conf || !addId) return;
    try {
      await sipPhoneApi.addMember(conf.id, addId);
      message.success('成员已邀请, 已推送会议通知');
      setAdding(false);
      setAddId('');
    } catch {
      message.error('邀请失败');
    }
  };

  const handleLeave = () => {
    sipService.endConference();
    setConf(null);
    setMessages([]);
  };

  const send = async () => {
    if (!conf || !draft.trim()) return;
    const body = draft.trim();
    setDraft('');
    try {
      const sent = await sipPhoneApi.sendMessage(conf.id, body);
      setMessages((prev) => appendUniqueMessage(prev, sent));
      setTimeout(() => scrollRef.current?.scrollTo({ top: 999999 }), 30);
    } catch {
      message.error('发送失败');
      setDraft(body);
    }
  };

  if (!conf) {
    return (
      <div>
        <Empty
          image={<TeamOutlined style={{ fontSize: 48, color: '#bbb' }} />}
          description="尚未加入任何会议"
        />
        <Space direction="vertical" style={{ width: '100%', marginTop: 12 }}>
          <Button
            type="primary"
            block
            icon={<VideoCameraAddOutlined />}
            onClick={() => setCreating(true)}
          >
            创建新会议
          </Button>
          <Button block icon={<TeamOutlined />} onClick={() => setJoining(true)}>
            通过房间码加入
          </Button>
        </Space>

        <Modal
          title="创建会议房间"
          open={creating}
          onCancel={() => setCreating(false)}
          onOk={handleCreate}
          okText="创建"
          cancelText="取消"
          destroyOnClose
        >
          <Input
            placeholder="会议名 (留空自动生成)"
            value={confName}
            onChange={(e) => setConfName(e.target.value)}
            style={{ marginBottom: 8 }}
          />
          <Select
            mode="multiple"
            placeholder="邀请同事 (可选, 创建后还可继续邀请)"
            style={{ width: '100%' }}
            value={invitedIds}
            onChange={setInvitedIds}
            showSearch
            optionFilterProp="label"
            options={users.map((u) => ({ label: userOptionLabel(u), value: u.id }))}
          />
        </Modal>

        <Modal
          title="加入会议"
          open={joining}
          onCancel={() => setJoining(false)}
          onOk={handleJoin}
          okText="加入"
          cancelText="取消"
        >
          <Input
            placeholder="输入 8 位房间码 (例 ABCD1234)"
            value={joinCode}
            onChange={(e) => setJoinCode(e.target.value.toUpperCase())}
            maxLength={8}
            style={{ textTransform: 'uppercase' }}
          />
        </Modal>
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: 460 }}>
      <Space style={{ marginBottom: 8 }}>
        <Typography.Text strong>{conf.name}</Typography.Text>
        <Tag color="purple">房间码 {conf.roomCode}</Tag>
        <Button size="small" danger icon={<PoweroffOutlined />} onClick={handleLeave}>
          退出会议
        </Button>
      </Space>

      <Space style={{ marginBottom: 8 }}>
        <Button icon={<UserAddOutlined />} onClick={() => setAdding(true)}>
          邀请成员
        </Button>
        <Typography.Text type="secondary" style={{ fontSize: 12 }}>
          成员通过会议通知加入同一个 FreeSWITCH 会议桥
        </Typography.Text>
      </Space>

      {sipState.conferenceLines.length > 0 && (
        <div style={{ marginBottom: 8 }}>
          <Typography.Text type="secondary" style={{ fontSize: 12 }}>
            会议桥状态
          </Typography.Text>
          <List
            size="small"
            dataSource={sipState.conferenceLines}
            renderItem={(line) => (
              <List.Item
                style={{ padding: '4px 6px' }}
                actions={[
                  <Button
                    key="hangup"
                    danger
                    size="small"
                    icon={<CloseOutlined />}
                    onClick={() => sipService.hangupConferenceLine(line.id)}
                  />,
                ]}
              >
                <List.Item.Meta
                  avatar={
                    <Avatar
                      icon={<PhoneOutlined />}
                      size="small"
                      style={{ background: '#52c41a' }}
                    />
                  }
                  title={
                    <Typography.Text style={{ fontSize: 12 }}>
                      FreeSWITCH {line.remote}
                    </Typography.Text>
                  }
                  description={
                    <Tag
                      style={{ fontSize: 10 }}
                      color={
                        line.state === 'in-call'
                          ? 'green'
                          : line.state === 'calling'
                            ? 'blue'
                            : 'red'
                      }
                    >
                      {line.state === 'in-call'
                        ? '通话中'
                        : line.state === 'calling'
                          ? '振铃中'
                          : line.state}
                    </Tag>
                  }
                />
              </List.Item>
            )}
          />
        </div>
      )}

      <div
        ref={scrollRef}
        style={{
          flex: 1,
          overflowY: 'auto',
          border: '1px solid #f0f0f0',
          borderRadius: 6,
          padding: 8,
          background: '#fafafa',
          minHeight: 100,
        }}
      >
        <Spin spinning={false}>
          {messages.length === 0 ? (
            <Empty description="会议聊天" />
          ) : (
            messages.map((m) => (
              <div key={m.id} style={{ marginBottom: 6 }}>
                <Typography.Text type="secondary" style={{ fontSize: 10 }}>
                  {m.senderName} · {fmtTime(m.sentAt)}
                </Typography.Text>
                <div
                  style={{
                    padding: '4px 8px',
                    borderRadius: 6,
                    background: '#fff',
                    border: '1px solid #e8e8e8',
                    marginTop: 2,
                  }}
                >
                  {m.body}
                </div>
              </div>
            ))
          )}
        </Spin>
      </div>
      <Space.Compact style={{ marginTop: 8 }}>
        <Input
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onPressEnter={send}
          placeholder="会议讨论..."
        />
        <Button type="primary" icon={<SendOutlined />} onClick={send}>
          发送
        </Button>
      </Space.Compact>

      <Modal
        title="邀请新成员加入会议"
        open={adding}
        onCancel={() => setAdding(false)}
        onOk={handleAddMember}
        okText="邀请"
        cancelText="取消"
        destroyOnClose
      >
        <Select
          placeholder="选择同事"
          style={{ width: '100%' }}
          value={addId || undefined}
          onChange={setAddId}
          showSearch
          optionFilterProp="label"
          options={users.map((u) => ({ label: userOptionLabel(u), value: u.id }))}
        />
      </Modal>
    </div>
  );
}
